import React from "react";
import styled from "styled-components";

const FooterWrapper = styled.footer`
  background-color: var(--primary-color);
  color: var(--text-light);
  padding: 3rem 0 1.5rem;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const FooterColumn = styled.div`
  flex: 1;
  min-width: 200px;
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const LogoImg = styled.img`
  height: 40px;
  margin-right: 10px;
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ClinicName = styled.span`
  font-family: "Libre Baskerville", serif;
  font-size: 1.5rem;
  color: var(--text-light);
  line-height: 1.2;
`;

const DentalText = styled.span`
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  color: var(--accent-color);
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const FooterTitle = styled.h5`
  font-family: "Montserrat", sans-serif;
  font-size: 1.2rem;
  margin-bottom: 1.2rem;
  color: var(--text-light);
`;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FooterItem = styled.li`
  margin-bottom: 0.8rem;
  font-family: "Lato", sans-serif;
`;

const FooterLink = styled.a`
  color: var(--text-light);
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--accent-color);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--text-light);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--accent-color);
    transform: translateY(-3px);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Divider = styled.hr`
  border: 0;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.1);
  margin: 1.5rem 0;
`;

const Footer = () => {
  return (
    <FooterWrapper>
      <FooterContainer>
        <FooterContent>
          <FooterColumn>
            <FooterLogo>
              <LogoImg src="/images/tooth.png" alt="Finch Dudley Dental Logo" />
              <LogoText>
                <ClinicName>Finch & Dudley</ClinicName>
                <DentalText>DENTAL</DentalText>
              </LogoText>
            </FooterLogo>
            <p>Providing exceptional dental care to North York and surrounding areas. Our commitment is to your dental health and beautiful smile.</p>
            <SocialLinks>
              <SocialLink
                href="https://www.facebook.com/share/1EP8GXaYvd/"
                aria-label="Facebook"
                target="_blank"
                rel="noreferrer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 320 512">
                  <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"/>
                </svg>
              </SocialLink>
              <SocialLink
                href="https://www.instagram.com/finch_dudley_dental"
                aria-label="Instagram"
                target="_blank"
                rel="noreferrer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 448 512">
                  <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
                </svg>
              </SocialLink>
            </SocialLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterList>
              <FooterItem>
                <FooterLink href="#home">Home</FooterLink>
              </FooterItem>
              <FooterItem>
                <FooterLink href="#services">Services</FooterLink>
              </FooterItem>
              <FooterItem>
                <FooterLink href="#about">About Us</FooterLink>
              </FooterItem>
              <FooterItem>
                <FooterLink href="#contact">Contact</FooterLink>
              </FooterItem>
            </FooterList>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Services</FooterTitle>
            <FooterList>
              <FooterItem>
                <FooterLink href="#services">General Dentistry</FooterLink>
              </FooterItem>
              <FooterItem>
                <FooterLink href="#services">Cosmetic Dentistry</FooterLink>
              </FooterItem>
              <FooterItem>
                <FooterLink href="#services">Restorative Dentistry</FooterLink>
              </FooterItem>
            </FooterList>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Contact Us</FooterTitle>
            <FooterList>
              <FooterItem>
                Lower Level, 78 Finch Avenue East,<br />
                North York, Ontario, M2N 7J5
              </FooterItem>
              <FooterItem>
                <FooterLink href="tel:4162250808">416-225-0808</FooterLink>
              </FooterItem>
              <FooterItem>
                <FooterLink href="mailto:info@finchdudley.com">info@finchdudley.com</FooterLink>
              </FooterItem>
            </FooterList>
          </FooterColumn>
        </FooterContent>
        
        <Divider />
        

      </FooterContainer>
    </FooterWrapper>
  );
};

export default Footer; 